import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { FC, useMemo } from 'react';
import { LuArrowRight } from 'react-icons/lu';
import { Counter } from 'src/components/Counter';
import { useWeightInput } from 'src/hooks/useWeightInput';
import { Order, Cart, trpc, OrderContainer } from 'src/trpc';
import { MoneyFromCents, safeMoneyFromCents } from '@libs/services/money';
import { PRICE_PER_TON_IN_CENT, MINIMUM_WEIGHT_IN_TONS } from 'src/constants';
import { NumericFormat } from 'react-number-format';

interface PayAsYouGoPricingState {
  weight: number;
  additionalMaterials: { [materialId: string]: number };
  usageByContainer: { [containerId: string]: number };
}

interface Props {
  order: Order;
  containers: OrderContainer[];
  cart: Cart;
  state: PayAsYouGoPricingState;
  updateState: (updates: Partial<PayAsYouGoPricingState>) => void;
  organization?: { minimumOrderWeightInTons?: number; pricePerTonInCents?: number };
}

export const PayAsYouGoPricingForm: FC<Props> = ({
  order,
  containers,
  cart,
  state,
  updateState,
  organization,
}) => {
  const { weightInput, setWeightInput, commitWeight } = useWeightInput(state.weight);

  const { data: materials } = trpc.admin.material.getMaterials.useQuery(undefined, {
    staleTime: Infinity,
    gcTime: Infinity,
    suspense: false,
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  // Use organization-specific minimum weight or fallback to system default
  const minWeightInTons =
    order.minimumWeightInTons || organization?.minimumOrderWeightInTons || MINIMUM_WEIGHT_IN_TONS;

  // Extract Pay As You Go pricing details from quotedPricingDetails
  const payAsYouGoDetails = useMemo(() => {
    const containerPricing = order.quotedPricingDetails?.quote?.pricing?.containers?.[0];
    const materialPricing = order.quotedPricingDetails?.quote?.pricing?.materials;

    const perTonRateInCents =
      materialPricing?.perTonRateInCents ||
      order.pricePerTonInCents ||
      organization?.pricePerTonInCents ||
      PRICE_PER_TON_IN_CENT;

    const dailyRateInCents =
      containerPricing?.totalDays && containerPricing?.adjustedPriceInCents
        ? Math.round(containerPricing.adjustedPriceInCents / containerPricing.totalDays)
        : 0;

    return {
      perTonRateInCents,
      dailyRateInCents,
    };
  }, [order.quotedPricingDetails, order.pricePerTonInCents, organization]);

  const initialWeight = useMemo(() => order.initialWeightInTons || 0, [order.initialWeightInTons]);

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* Pay As You Go Weight Section */}
      <div className="flex flex-col gap-2 w-full">
        <label className="text-sm text-muted">Final Weight in Tons</label>
        <div className="flex flex-row gap-2 text-sm text-muted">
          <NumericFormat
            step={0.1}
            thousandSeparator={true}
            type="text"
            inputMode="decimal"
            className="w-full"
            value={weightInput}
            onValueChange={(values) => setWeightInput(values.value)}
            onBlur={() => commitWeight(updateState)}
            customInput={Input}
            placeholder="Enter weight"
            valueIsNumericString={true}
            decimalScale={2}
            allowNegative={false}
            name="weight"
          />
          <div className="flex flex-row gap-2 items-center">
            <>
              <span className="line-through">
                {MoneyFromCents(cart.lineItems?.materials || 0).toFormat()}
              </span>
              <Badge className="text-xs" variant="success">
                Paid
              </Badge>
            </>

            {state.weight !== Math.max(initialWeight, minWeightInTons) && (
              <>
                {/* Sized in rem rather than the react-icons 1em default, so the arrow holds
                    its size when the order detail page scales its type on mobile. */}
                <LuArrowRight className="w-4 h-4 flex-shrink-0" />
                <span>
                  {safeMoneyFromCents(
                    (state.weight || 0) * payAsYouGoDetails.perTonRateInCents,
                  ).toFormat()}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Container Days Section */}
      <div className="flex flex-col gap-2 w-full text-sm text-muted">
        <label className="text-sm text-muted">Days Per Container</label>
        {containers.map((c) => (
          <div key={c._id} className="flex flex-row w-full items-center gap-4">
            <label className="whitespace-nowrap">
              {c.friendlyId} ({c.containerTypeInfo.size})
            </label>
            <NumericFormat
              thousandSeparator={true}
              type="text"
              inputMode="numeric"
              className="w-full"
              min={0}
              value={state.usageByContainer[c._id] || 0}
              onValueChange={(values) =>
                updateState({
                  usageByContainer: {
                    ...state.usageByContainer,
                    [c._id]: values.floatValue || 0,
                  },
                })
              }
              customInput={Input}
              placeholder="0"
              valueIsNumericString={true}
              decimalScale={0}
              name={c._id}
              step={1}
            />

            <span>
              {MoneyFromCents(
                (state.usageByContainer[c._id] || 0) *
                  (payAsYouGoDetails.dailyRateInCents > 0
                    ? payAsYouGoDetails.dailyRateInCents
                    : c.containerTypeInfo.pricePerDay),
              ).toFormat()}
            </span>
          </div>
        ))}
      </div>

      <div className="h-[1px] w-full bg-disabled"></div>

      {/* Additional Materials Section */}
      <h4 className="font-semibold text-base">Additional Materials</h4>
      <div className="flex flex-col gap-2.5 max-h-96 overflow-y-auto">
        {!materials && <div className="text-sm text-muted">Loading materials...</div>}
        {materials && materials.length === 0 && (
          <div className="text-sm text-muted">No materials available</div>
        )}
        {materials && materials.filter((m) => m.extraChargeInCents > 0).length === 0 && (
          <div className="text-sm text-muted">No materials with additional charges</div>
        )}
        {materials
          ?.sort((a, b) => a.name.localeCompare(b.name))
          ?.filter((m) => m.extraChargeInCents > 0)
          .map((material) => (
            <div
              key={material._id}
              className="flex flex-row gap-2 w-full items-center text-muted font-medium text-base"
            >
              <span className="flex-1">{material.name}</span>
              <div className="flex-1 flex flex-row gap-4 items-center">
                <Counter
                  onChange={(e) =>
                    updateState({
                      additionalMaterials: {
                        ...state.additionalMaterials,
                        [material._id]: e,
                      },
                    })
                  }
                  value={state.additionalMaterials[material._id] || 0}
                />
              </div>
              <p className="min-w-16">
                {MoneyFromCents(
                  (state.additionalMaterials[material._id] || 0) * material.extraChargeInCents,
                ).toFormat()}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
};
